import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

export interface Theme {
  id: string;
  name: string;
  description: string;
  preview: string;
  styles: {
    primaryColor: string;
    secondaryColor: string;
    fontFamily: string;
    borderRadius: string;
  };
}

const themes: Theme[] = [
  {
    id: 'professional',
    name: 'Professional',
    description: 'Clean and modern design suitable for business and professional courses',
    preview: '/themes/professional-preview.png',
    styles: {
      primaryColor: '#2563eb',
      secondaryColor: '#1e40af',
      fontFamily: 'Inter',
      borderRadius: '0.375rem',
    },
  },
  {
    id: 'children',
    name: 'Children',
    description: 'Colorful and playful design perfect for young learners',
    preview: '/themes/children-preview.png',
    styles: {
      primaryColor: '#f59e0b',
      secondaryColor: '#d97706',
      fontFamily: 'Comic Sans MS',
      borderRadius: '0.75rem',
    },
  },
  {
    id: 'quizizz',
    name: 'Quizizz',
    description: 'Gamified learning experience with interactive elements',
    preview: '/themes/quizizz-preview.png',
    styles: {
      primaryColor: '#7c3aed',
      secondaryColor: '#6d28d9',
      fontFamily: 'Poppins',
      borderRadius: '1rem',
    },
  },
];

interface ThemeSelectionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTheme: (theme: Theme) => void;
  currentTheme?: Theme;
}

export function ThemeSelectionDialog({
  isOpen,
  onClose,
  onSelectTheme,
  currentTheme,
}: ThemeSelectionDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select Course Theme</DialogTitle>
          <DialogDescription>
            Choose a theme that best matches your course content and audience
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {themes.map((theme) => (
            <div
              key={theme.id}
              className={`cursor-pointer transition-all hover:shadow-lg ${
                currentTheme?.id === theme.id ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => onSelectTheme(theme)}
            >
              <Card>
                <CardContent className="p-4">
                  <div className="aspect-video w-full overflow-hidden rounded-md bg-gray-100">
                    <img
                      src={theme.preview}
                      alt={theme.name}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-semibold">{theme.name}</h3>
                    <p className="mt-1 text-sm text-gray-500">{theme.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div
                          className="h-4 w-4 rounded-full"
                          style={{ backgroundColor: theme.styles.primaryColor }}
                        />
                        <div
                          className="h-4 w-4 rounded-full"
                          style={{ backgroundColor: theme.styles.secondaryColor }}
                        />
                      </div>
                      {currentTheme?.id === theme.id && (
                        <span className="text-sm font-medium text-blue-600">Current Theme</span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
} 